import axios, { RawAxiosRequestHeaders, CreateAxiosDefaults } from 'axios';
import { InvalidTokenException } from '@/models/Exceptions';

/**
 * The headers for the custom AXIOS client.
 * @interface AxiosClientOptionHeaders
 */
interface AxiosClientOptionHeaders extends RawAxiosRequestHeaders {
  /**
   * The API key for requests made by the AXIOS client.
   * @type {string | undefined}
   */
  'x-api-key'?: string;
}

/**
 * The options for the custom AXIOS client function.
 * @interface AxiosClientOptions
 */
interface AxiosClientOptions extends CreateAxiosDefaults {
  /**
   * All headers for the AXIOS client.
   * @type {AxiosClientOptionHeaders | undefined}
   */
  headers?: AxiosClientOptionHeaders;
}

/**
 * This function creates a custom axios client that has a response interceptor
 * that checks for 401 and 403 errors and throws an InvalidTokenException.
 * This is useful for handling token expiration.
 * @param options - The options for the custom axios client.
 * @returns AxiosInstance - The custom axios client.
 */
export const getCustomAxiosClient = (options: AxiosClientOptions) => {
  const customAxiosClient = axios.create(options);

  // Add a response interceptor
  customAxiosClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response.status === 401 || error.response.status === 403) {
        return Promise.reject(new InvalidTokenException());
      }
      return Promise.reject(error);
    },
  );
  return customAxiosClient;
};
